/**
 * Alpine Store: UI Surface Registry v1.0.0
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * IIFE providing `$store.ui` — a live registry of mounted UI surfaces (drawers,
 * popovers, modals, etc). Surface web components self-register in their
 * `connectedCallback` and unregister in `disconnectedCallback`.
 *
 * MENTAL MODEL
 * ─────────────────────────────────────────────────────────────────────────────
 * The component owns truth. The registry is a getter lens that reads back into
 * the component's DOM state on demand. There is no `cartOpen: boolean` flag —
 * if you want to know whether the cart drawer is open, you ask the cart drawer
 * (via `$store.ui.isOpen('cart-drawer')`, which reads the registered surface's
 * `isOpen` getter).
 *
 * Why? Two flag-and-effect setups (one in the store, one in the component) used
 * to drift. The component would close itself but the flag stayed `true`; the
 * flag would flip but the component had unmounted. With a single source of
 * truth on the component, drift is impossible.
 *
 * REGISTRATION CONTRACT
 * ─────────────────────────────────────────────────────────────────────────────
 *   $store.ui.register(id, {
 *     type:    'drawer' | 'popover' | 'modal' | ...,    // free-form string
 *     element: HTMLElement,                              // the surface element
 *     get isOpen() { return <bool>; },                   // truth read
 *     open():    void | Promise<void>,                   // action delegate
 *     close():   void | Promise<void>,                   // action delegate
 *     meta?:     object,                                 // free-form per surface
 *   });
 *
 * Re-registering the same `id` replaces the previous entry (the common case is
 * a surface re-mount after section morph, where the new instance supersedes
 * the old).
 *
 * QUERIES
 * ─────────────────────────────────────────────────────────────────────────────
 *   $store.ui.isOpen(id)        — boolean, reads surface.isOpen
 *   $store.ui.byType(type)      — array of registered surface refs of `type`
 *   $store.ui.anyOpen           — boolean, true if any surface is open
 *   $store.ui.get(id)           — return the registered ref or undefined
 *
 * ACTIONS
 * ─────────────────────────────────────────────────────────────────────────────
 *   $store.ui.open(id)          — delegate to surface.open()
 *   $store.ui.close(id)         — delegate to surface.close()
 *   $store.ui.closeAll()        — close every open surface
 *
 * EVENTS
 * ─────────────────────────────────────────────────────────────────────────────
 * Dispatches the prepared Slice 1 event classes:
 *   register(id)    → UISurfaceRegisteredEvent
 *   open(id)        → UISurfaceOpenedEvent
 *   close(id)       → UISurfaceClosedEvent
 *
 * Each event auto-logs via `Logger.ns('events')` (ThemeEvent base), so tests
 * can observe surface lifecycle with `themePage.waitForEvent('events', '…')`.
 *
 * LOAD ORDER
 * ─────────────────────────────────────────────────────────────────────────────
 * Must run AFTER `logger.js` and `events.js`, BEFORE `alpine-stores.js` (so the
 * `ui` store is registered before any other alpine:init handler can ask for
 * it). Loaded via `<script defer src="alpine-store-ui.js">` in scripts.liquid.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 */

(function () {
  'use strict';

  window.Theme = window.Theme || {};

  const Logger = window.Theme.Logger || {
    ns: () => ({
      debug: () => {},
      info: () => {},
      warn: console.warn.bind(console),
      error: console.error.bind(console),
    }),
  };
  const log = Logger.ns('store.ui');

  // Event classes from the single-source taxonomy (events.js IIFE, loaded
  // before this file). If they're missing, that's a wiring bug — log and
  // fall back to no-op dispatch.
  const Events = window.Theme.events || null;
  if (!Events) {
    log.error('window.Theme.events not found — events.js must load before alpine-store-ui.js');
  }
  const UIRegistered = Events?.UISurfaceRegisteredEvent;
  const UIOpened = Events?.UISurfaceOpenedEvent;
  const UIClosed = Events?.UISurfaceClosedEvent;

  const dispatch = (EventClass, ...args) => {
    if (!EventClass) return;
    try {
      document.dispatchEvent(new EventClass(...args));
    } catch (err) {
      log.error('Failed to dispatch surface event', err);
    }
  };

  const setupStore = () => {
    if (typeof Alpine === 'undefined') {
      log.error('Alpine not loaded — cannot register $store.ui');
      return;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Surface store
    // ─────────────────────────────────────────────────────────────────────────
    //
    // `surfaces` is a plain object keyed by surface id. Each entry is the
    // registration ref supplied by the surface component. Reads go through
    // accessor methods so callers don't depend on the shape directly.
    //
    // ─────────────────────────────────────────────────────────────────────────

    Alpine.store('ui', {
      // Public-but-stable: callers can iterate via `byType` / `Object.keys`,
      // but reads of open-state must go through `isOpen(id)` so the reactive
      // tick is registered as a dependency.
      surfaces: {},

      // Reactive tick — bumped whenever any surface flips open/closed. The
      // surface owns the truth (DOM attribute), but DOM attribute changes are
      // not observable by Alpine; the surface calls `notifyStateChange(id)`
      // from its open/close paths to wake reactive consumers like
      // `x-show="$store.ui.isOpen('cart-drawer')"`. See alpine-store-ui.js
      // header comment for the contract.
      _openTick: 0,

      // ─── Registration ───────────────────────────────────────────────────

      register(id, ref) {
        if (!id || typeof id !== 'string') {
          log.warn('register: id must be a non-empty string', { id });
          return;
        }
        if (!ref || typeof ref !== 'object') {
          log.warn('register: ref must be an object', { id, ref });
          return;
        }

        // Replace prior registration silently — this is the normal flow when a
        // section morph re-mounts the surface element.
        this.surfaces[id] = ref;
        // Bump the tick on register so anything bound to `isOpen(id)` re-reads.
        this._openTick++;
        log.debug('Registered', { id, type: ref.type });
        dispatch(UIRegistered, id, ref.type || 'unknown', ref.meta || {});
      },

      unregister(id) {
        if (!(id in this.surfaces)) return;
        delete this.surfaces[id];
        this._openTick++;
        log.debug('Unregistered', { id });
      },

      /**
       * Surfaces call this from their open/close paths (including drag-close,
       * Escape, overlay click) so reactive consumers re-read isOpen(id).
       *
       * The truth stays on the surface (DOM attribute); this just wakes Alpine.
       */
      notifyStateChange(id) {
        // Read-after-write to ensure Alpine sees the mutation. We don't need
        // to know the new state — the tick is the signal; consumers re-read
        // via isOpen(id).
        this._openTick++;
        // No event dispatch here — UISurfaceOpenedEvent / Closed are emitted
        // by open()/close() when the registry initiates. Direct user paths
        // (Escape, drag) get their own observability via the surface's logs.
      },

      // ─── Queries ────────────────────────────────────────────────────────

      get(id) {
        return this.surfaces[id];
      },

      /**
       * Returns whether a surface is currently open.
       *
       * Touches `_openTick` so Alpine tracks this as a reactive dependency,
       * then reads through the surface's own `isOpen` getter (truth).
       */
      isOpen(id) {
        // Register reactive dependency before touching surface.
        // eslint-disable-next-line no-unused-expressions
        this._openTick;
        const ref = this.surfaces[id];
        if (!ref) return false;
        try {
          return !!ref.isOpen;
        } catch (err) {
          log.warn('isOpen read threw', { id, err });
          return false;
        }
      },

      byType(type) {
        return Object.values(this.surfaces).filter((ref) => ref.type === type);
      },

      get anyOpen() {
        // Touch the tick for reactivity.
        // eslint-disable-next-line no-unused-expressions
        this._openTick;
        return Object.values(this.surfaces).some((ref) => {
          try {
            return !!ref.isOpen;
          } catch {
            return false;
          }
        });
      },

      // ─── Actions ────────────────────────────────────────────────────────

      open(id) {
        const ref = this.surfaces[id];
        if (!ref) {
          log.warn('open: no surface registered', { id });
          return;
        }
        if (typeof ref.open !== 'function') {
          log.warn('open: surface has no open() method', { id });
          return;
        }
        try {
          const result = ref.open();
          // Surfaces also call notifyStateChange themselves — but in case a
          // surface forgets, this guarantees registry-initiated opens
          // propagate to reactive consumers.
          this._openTick++;
          dispatch(UIOpened, id, ref.type || 'unknown', ref.meta || {});
          return result;
        } catch (err) {
          log.error('open: surface threw', { id, err });
        }
      },

      close(id) {
        const ref = this.surfaces[id];
        if (!ref) return;
        if (typeof ref.close !== 'function') {
          log.warn('close: surface has no close() method', { id });
          return;
        }
        try {
          const result = ref.close();
          this._openTick++;
          dispatch(UIClosed, id, ref.type || 'unknown', ref.meta || {});
          return result;
        } catch (err) {
          log.error('close: surface threw', { id, err });
        }
      },

      closeAll() {
        for (const id of Object.keys(this.surfaces)) {
          if (this.isOpen(id)) this.close(id);
        }
      },
    });

    log.info('$store.ui registered');
  };

  document.addEventListener('alpine:init', setupStore);

  log.info('alpine-store-ui v1.0.0 loaded');
})();
